/**
 * Invoice Audit Agent — single source of truth for every number shared across
 * this set's surfaces (chat scripts, insights, dashboard, mock agents).
 *
 * SHARED WORLD: this set is the finance chair of the same fictional company as
 * `sets/cascade-3pl` — Cascade 3PL, ~$165M/yr revenue, 4 fulfillment centers,
 * carriers FedEx / UPS / USPS / OnTrac, largest customer Northwind Apparel.
 * Keep those facts in agreement with the ops set. NOTE: invoice-audit
 * *leakage* (carrier billing errors) is a DISTINCT concept from the ops set's
 * carrier *claims* (damage/loss dollars). Never let the two read as the same
 * number.
 *
 * Math audit (all rows reconcile — see docs/demos/invoice-audit.md §1):
 *   monthly recovery run-rate = spend × leakage = $6.0M × 1.4% = ~$84K
 *   annualized                = ~$84K × 12       = ~$1.0M
 *   single FedEx week found   = $347K × 1.4%     = ~$4,860
 *   90-day found              = 3 × monthly       = ~$260K = $198K claimable + $62K expired
 *   per-carrier rows sum to the 90-day totals; each ≈ 1.44% of its 90-day spend
 *   per-error-type rows sum to the single-file found total
 */

// ---------------------------------------------------------------------------
// Economics anchor
// ---------------------------------------------------------------------------

export const AUDIT_ECONOMICS = {
  monthlyCarrierSpend: 6_000_000,
  leakagePct: 1.4,
  monthlyRecoveryRunRate: 84_000,
  annualizedRecovery: 1_000_000,
  /** Parcel-audit firms' typical contingency cut, for the Before/After foil. */
  auditFirmContingencyPctLow: 25,
  auditFirmContingencyPctHigh: 35,
} as const

// ---------------------------------------------------------------------------
// Act 1 — one FedEx weekly invoice vs the rate card
// ---------------------------------------------------------------------------

export const ACT1_FILE = {
  carrier: 'FedEx',
  weekLabel: 'Week 24 (Jun 9–15)',
  lineItems: 46_000,
  billed: 347_000,
  flaggedLines: 357,
  found: 4_860,
  disputeDraftCount: 10,
} as const

export type ErrorTypeRow = {
  id: string
  label: string
  count: number
  dollars: number
  /** severity drives row accent; derived from dollar rank, not hand-set on a whim */
  status: 'critical' | 'warning' | 'neutral'
}

/** Sums to ACT1_FILE.found ($4,860) and ACT1_FILE.flaggedLines (357). */
export const ACT1_ERROR_TYPES: ErrorTypeRow[] = [
  {
    id: 'dup-address-correction',
    label: 'Duplicate address-correction fees',
    count: 132,
    dollars: 2_749, // ~$20.83/line — realistic FedEx address-correction fee
    status: 'critical',
  },
  {
    id: 'service-level',
    label: 'Service-level mismatches (2Day billed · Ground contracted)',
    count: 41,
    dollars: 1_110, // ~$27/line — realistic 2Day-vs-Ground delta
    status: 'warning',
  },
  {
    id: 'residential-surcharge',
    label: 'Misapplied residential surcharges',
    count: 96,
    dollars: 571, // ~$5.95/line — the flat residential surcharge
    status: 'warning',
  },
  {
    id: 'dim-rounding',
    label: 'DIM-weight rounding overcharges',
    count: 88,
    dollars: 430,
    status: 'neutral',
  },
]

/** One worked line-item, shown in the Act 1 evidence drill (billed vs contract). */
export const ACT1_EVIDENCE = {
  trackingNumber: '7789 4412 0087',
  accessorialCode: 'ADDR-CORR',
  accessorialLabel: 'Address correction',
  billed: 19.3,
  contract: 0.0,
  delta: 19.3,
  note: 'Same shipment billed the address-correction fee twice — the rate card allows it once.',
} as const

// ---------------------------------------------------------------------------
// Act 2 — 90-day sweep across all four carriers
// ---------------------------------------------------------------------------

export const SWEEP_90D = {
  carrierCount: 4,
  weeks: 13,
  lineItems: 2_400_000,
  found: 260_000,
  claimable: 198_000,
  expired: 62_000,
  /** The urgency lever: next tranche of claimable dollars that expires soon.
   *  Deliberately NOT equal to any single carrier's `expired` value (a
   *  same-number-different-meaning collision a viewer would trip on). */
  expiringSoon: 21_000,
  expiringWindowDays: 6,
} as const

export type CarrierRecoveryRow = {
  carrier: string
  spend90d: number
  found: number
  claimable: number
  expired: number
}

/**
 * Per-carrier 90-day recovery. Same 4 carriers as the ops set (`CARRIER_PERFORMANCE`).
 * Columns sum to SWEEP_90D: spend 18.0M · found 260K · claimable 198K · expired 62K.
 * Each carrier's found ≈ 1.44% of its 90-day spend.
 */
export const CARRIER_RECOVERY: CarrierRecoveryRow[] = [
  {
    carrier: 'FedEx',
    spend90d: 7_200_000,
    found: 104_000,
    claimable: 80_000,
    expired: 24_000,
  },
  {
    carrier: 'UPS',
    spend90d: 5_400_000,
    found: 78_000,
    claimable: 60_000,
    expired: 18_000,
  },
  {
    carrier: 'USPS',
    spend90d: 3_600_000,
    found: 52_000,
    claimable: 40_000,
    expired: 12_000,
  },
  {
    carrier: 'OnTrac',
    spend90d: 1_800_000,
    found: 26_000,
    claimable: 18_000,
    expired: 8_000,
  },
]

/**
 * The two paths in the Act 2 decision card. Both sized.
 * A = dispute what's claimable now (one-time). B = dispute + always-on audit +
 * root-cause fix (annualized).
 */
export const ACT2_PATHS = {
  disputeOnly: {
    oneTimeRecovery: 198_000,
    continuedLeakageMonthly: 84_000,
  },
  alwaysOn: {
    oneTimeRecovery: 198_000,
    annualizedRecovered: 1_000_000,
  },
} as const

// ---------------------------------------------------------------------------
// Root-cause driver (Act 2 pattern card + insight 3)
// ---------------------------------------------------------------------------

export const ADDRESS_FEED_ROOT_CAUSE = {
  brand: 'Northwind Apparel', // Cascade's largest customer (shared world)
  /** Share of duplicate address-correction fees traced to this brand's feed. */
  sharePct: 61,
  fixTarget: "the brand's address-validation feed",
} as const

// ---------------------------------------------------------------------------
// Standing agents (Agents tab) — one weekly auditor per carrier.
// $ found per run derives from each carrier's 90-day found ÷ 13 weeks.
// ---------------------------------------------------------------------------

export const AUDIT_AGENTS = CARRIER_RECOVERY.map((c) => ({
  carrier: c.carrier,
  weeklyFound: Math.round(c.found / SWEEP_90D.weeks),
}))
